/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { EmailRegex } from '@msdyn365-commerce-modules/retail-actions';
import { getTelemetryObject, IModuleProps, INodeProps, ITelemetryContent } from '@msdyn365-commerce-modules/utilities';
import { ContentEditableEvent, getUrlSync, RichTextComponent, Text } from '@msdyn365-commerce/core';
import { Address, BusinessPartnerProspect, CommerceProperty } from '@msdyn365-commerce/retail-proxy';
import { createProspectAsync } from '@msdyn365-commerce/retail-proxy/dist/DataActions/BusinessPartnersDataActions.g';
import classnames from 'classnames';
import * as React from 'react';

import { IBusinessSignUpConfig, IBusinessSignUpProps, IBusinessSignUpResources } from './business-sign-up.props.autogenerated';
import { SignUpButton, SignUpError, SignUpInput, SignUpLabel } from './components';
import { IBusinessSignUpRadioButtonOptionProps } from './components/sign-up-radio-button';

export interface IBusinessSignUpExtendedProps extends IBusinessSignUpProps<IBusinessSignUpConfig> {
    formItems?: IFormItem[];
}

export interface BusinessPartnerProspectExtended extends BusinessPartnerProspect {
    VatNumber?: string;
    Interessts?: string[];
}

export interface IBusinessSignUpItem {
    wrapper: INodeProps;
    isAddressInput?: boolean;
    id: string;
    label: React.ReactNode;
    errorMessage: React.ReactNode;
    input?: React.ReactNode;
}

export interface IBusinessState {
    isReady: boolean;
    formItems: IFormItem[];
    addressHasErrors: boolean;
    showAddressErrors: boolean;
    hasSubmitErrors: boolean;
    captchaReady: boolean;
}

export interface IBusinessSignUpLocalAccount {
    FormWrapper: INodeProps;
    items: IBusinessSignUpItem[];
    buttons: React.ReactNode[];
    errorMessage: React.ReactNode[];
    disclaimer: React.ReactNode;
}

export interface IBusinessSignUpViewProps {
    className: string;
    businessSignUp: IModuleProps;
    ContainerWrapper: INodeProps;
    heading: React.ReactNode;
    businessSignUpForm: IBusinessSignUpLocalAccount;
    formItems: IFormItem[];
    captchaReady: boolean;
    setCaptchaReady: (isReady: boolean) => void;
}

export interface IFormItem {
    id: string;
    type: InputType;
    label: string;
    value: string;
    maxChars?: string;
    isRequired: boolean;
    pattern?: string;
    address?: Address;
    customClass?: string;
    fieldReference?: React.RefObject<HTMLInputElement>;
    radioButtonOptions?: IBusinessSignUpRadioButtonOptionProps[];
    options?: { value: string; label: string }[];
    radioButtonGroupClass?: string;
}

export enum InputType {
    // eslint-disable-next-line @typescript-eslint/no-shadow -- Text enum doesn't override the imported Text
    Text = 'text',
    Address = 'address',
    Email = 'email',
    TextArea = 'textarea',
    RadioButton = 'radiobutton',
    Select = 'select'
}

export enum InputID {
    FirstName = 'FirstName',
    LastName = 'LastName',
    Email = 'CompanyEmail',
    CompanyName = 'CompanyName',
    Address = 'CompanyAddress',
    Department = 'Department',
    VatId = 'VatId',
    ProspectType = 'ProspectType'
}

enum RegistrationFields {
    countryIso = 'company_addressthreeletterisoregionname',
    vatLabel = 'VatId_label',
    cheCountry = 'CHE'
}

export const renderInputSection = (
    inputs: IFormItem[],
    className: string,
    needToReset: boolean,
    onInputChange: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => void,
    showErrors: boolean,
    resources: IBusinessSignUpResources,
    config: IBusinessSignUpConfig,
    addressForm?: React.ReactNode,
    updateAddress?: (errors: boolean, currAddress?: Address) => void
): IBusinessSignUpItem[] => {
    return inputs.map((element, index) => {
        const { id, type, label, maxChars, isRequired, pattern, customClass, value, fieldReference } = element;
        const baseSignUpItem: IBusinessSignUpItem = {
            wrapper: {
                className: classnames(className, `${className}-${id}`, customClass)
            },
            id,
            label: <SignUpLabel {...{ id, forId: id, className, text: label }} />,
            errorMessage: <SignUpError {...{ className, type: 'item' }} />
        };

        if (type === InputType.Address) {
            if (addressForm) {
                baseSignUpItem.isAddressInput = true;
                baseSignUpItem.input = (
                    <>
                        {React.cloneElement(addressForm as React.ReactElement, {
                            updateForm: updateAddress,
                            hasError: showErrors,
                            isRequired: isRequired === undefined ? false : isRequired,
                            resetAddress: needToReset
                        })}
                    </>
                );
            } else {
                // If the address slot is not filled, remove this input from the form
                inputs.splice(index, 1);
            }
        } else if (type === InputType.Select) {
            baseSignUpItem.input = (
                <select id={id} className='msc-address-form__dropdown' value={value} onChange={onInputChange} required={isRequired}>
                    <option value='' disabled></option>
                    {config.interesstDropdownItems?.map(option => (
                        <option key={option.dropdownText} value={option.dropdownText}>
                            {option.dropdownText}
                        </option>
                    ))}
                </select>
            );
        } else {
            let requiredCheck = isRequired;
            if (typeof window !== 'undefined' && document) {
                const countrySelect = document.getElementById(RegistrationFields.countryIso) as HTMLSelectElement;
                if (countrySelect) {
                    const vatLabel = document.getElementById(RegistrationFields.vatLabel);
                    if (id === InputID.VatId && countrySelect.value === RegistrationFields.cheCountry && vatLabel) {
                        requiredCheck = false;
                        vatLabel.innerHTML = `${resources.vatNumberLabel}`;
                    } else if (vatLabel) {
                        vatLabel.innerHTML = `${resources.vatNumberLabel} *`;
                    }
                }
            }

            baseSignUpItem.input = (
                <SignUpInput
                    {...{
                        id,
                        type,
                        value,
                        maxLength: maxChars,
                        pattern,
                        className,
                        isRequired: isRequired === undefined ? false : requiredCheck,
                        onChange: onInputChange,
                        fieldReference
                    }}
                />
            );
        }

        return baseSignUpItem;
    });
};

/**
 *
 * Business Request Form Modules
 * Form that a business can use to submit a request to become a partner with Microsoft.
 * @extends {React.Component<IBusinessSignUpProps<IBusinessSignUpConfig>>}
 */
class BusinessSignUp extends React.Component<IBusinessSignUpExtendedProps, IBusinessState> {
    private readonly moduleClassName: string = 'ms-business-sign-up';

    // Used as default data
    // eslint-disable-next-line @typescript-eslint/member-ordering
    public BusinessSignUpDefaultInputs: IFormItem[] = [
        {
            id: InputID.FirstName,
            type: InputType.Text,
            label: this.props.resources.firstNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.LastName,
            type: InputType.Text,
            label: this.props.resources.lastNameLabel,
            value: '',
            isRequired: true,
            customClass: 'width-25'
        },
        {
            id: InputID.Email,
            type: InputType.Email,
            label: this.props.resources.emailAddressLabel,
            value: '',
            isRequired: true,
            pattern: EmailRegex.defaultRegex.source,
            customClass: 'width-50'
        },
        {
            id: InputID.CompanyName,
            type: InputType.Text,
            label: this.props.resources.companyLabel,
            value: '',
            isRequired: true,
            customClass: 'width-50'
        },
        {
            id: InputID.Department,
            type: InputType.Text,
            label: this.props.resources.departmentLabel,
            value: '',
            isRequired: false,
            customClass: 'width-50'
        },
        {
            id: InputID.Address,
            type: InputType.Address,
            label: this.props.resources.companyAddressLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.VatId,
            type: InputType.Text,
            label: this.props.resources.vatNumberLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        },
        {
            id: InputID.ProspectType,
            type: InputType.Select,
            label: this.props.resources.interestedInLabel,
            value: '',
            isRequired: true,
            customClass: 'width-100'
        }
    ];

    private readonly formChildReference: React.RefObject<HTMLInputElement> = React.createRef<HTMLInputElement>();

    private needsToReset: boolean = false;

    private readonly telemetryContent: ITelemetryContent;

    constructor(props: IBusinessSignUpExtendedProps) {
        super(props);

        this.state = {
            isReady: false,
            formItems: this.props.formItems || this._addedReference(),
            addressHasErrors: false,
            showAddressErrors: false,
            hasSubmitErrors: false,
            captchaReady: false
        };

        this.telemetryContent = getTelemetryObject(
            this.props.context.request.telemetryPageName!,
            this.props.friendlyName,
            this.props.telemetry
        );
    }

    public componentDidMount(): void {
        this.formChildReference.current?.focus();
    }

    public shouldComponentUpdate(nextProps: IBusinessSignUpExtendedProps, nextState: IBusinessState): boolean {
        if (this.state === nextState && this.props.data === nextProps.data) {
            return false;
        }
        return true;
    }

    public render(): JSX.Element {
        const { config } = this.props;
        const { formItems } = this.state;
        const { className, heading } = config;

        const viewProps = {
            ...this.props,
            businessSignUp: {
                moduleProps: this.props,
                className: classnames(this.moduleClassName, className),
                captchaReady: this.state.captchaReady
            },
            setCaptchaReady: this.setCaptchaReady,
            ContainerWrapper: {
                className: `${this.moduleClassName}__container`
            },
            heading: (
                <Text
                    className={`${this.moduleClassName}__heading`}
                    {...heading}
                    editProps={{ onEdit: this.handleHeadingChange, requestContext: this.props.context.request }}
                />
            ),
            businessSignUpForm: this._renderForm(),
            formItems
        };

        return this.props.renderView(viewProps) as React.ReactElement;
    }

    /**
     * Handle the heading change event.
     * @param event - Content Editable Event.
     */
    public handleHeadingChange = (event: ContentEditableEvent): void => {
        this.props.config.heading.text = event.target.value;
    };

    private _renderForm(): IBusinessSignUpLocalAccount {
        const { config, resources, slots } = this.props;
        const { showAddressErrors, hasSubmitErrors, isReady, formItems } = this.state;
        const {
            signUpButtonArialabel,
            signUpButtonText,
            cancelButtonArialabel,
            cancelButtonText,
            errorMessage,
            partialAddressErrorMessage
        } = resources;
        const addressForm = (slots && slots.addressSlot && slots.addressSlot.length > 0 && slots.addressSlot[0]) || undefined;

        const form = {
            FormWrapper: {
                className: `${this.moduleClassName}__form `
            },
            items: renderInputSection(
                formItems,
                `${this.moduleClassName}__form-item`,
                this.needsToReset,
                this._onInputChange.bind(this),
                showAddressErrors,
                resources,
                config,
                addressForm,
                this._onAddressChange
            ),
            buttons: [
                <SignUpButton
                    key='continue'
                    id='continue'
                    className={`${this.moduleClassName}__create-button`}
                    ariaLabel={signUpButtonArialabel}
                    text={signUpButtonText}
                    disabled={!isReady}
                    onClick={this._onSubmit}
                    telemetryContent={this.telemetryContent}
                />,
                <SignUpButton
                    key='cancel'
                    id='cancel'
                    className={`${this.moduleClassName}__cancel-button`}
                    ariaLabel={cancelButtonArialabel}
                    text={cancelButtonText}
                    onClick={this._onCancel}
                    telemetryContent={this.telemetryContent}
                />
            ],
            errorMessage: [
                hasSubmitErrors && <SignUpError key='error' id='error' className={this.moduleClassName} message={errorMessage} />,
                showAddressErrors && (
                    <SignUpError key='error' id='error' className={this.moduleClassName} message={partialAddressErrorMessage} />
                )
            ],
            disclaimer: config.disclaimer && (
                <RichTextComponent className={`${this.moduleClassName}__disclaimer`} text={config.disclaimer} />
            )
        };

        if (this.needsToReset) {
            this.needsToReset = false;
        }

        return form;
    }

    private _onInputChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>): void {
        const newForm = this.state.formItems;
        newForm.forEach(item => {
            if (item.id === event.target.id) {
                item.value = event.target.value;
            }
        });

        // Check if form is ready, update state object with latest values, values have changed so reset hasSubmitErrros
        this.setState({
            isReady: this._isFormReady(),
            formItems: newForm,
            hasSubmitErrors: false
        });
    }

    private _addedReference(): IFormItem[] {
        const newFormFields = this.BusinessSignUpDefaultInputs;
        newFormFields[0].fieldReference = this.formChildReference;
        return newFormFields;
    }

    private readonly _onAddressChange = (errors: boolean, currAddress?: any): void => {
        // Find address item
        const newForm = this.state.formItems;
        const addressItem = newForm.find(item => {
            return item.type === InputType.Address;
        });

        // If found --> set to currentAddress
        if (addressItem) {
            addressItem.address = currAddress;

            // Update state with new values and set errors to false if address is not required field
            this.setState(
                {
                    formItems: newForm,
                    addressHasErrors: errors,
                    showAddressErrors: false
                },
                () => {
                    this.setState({ isReady: this._isFormReady() });
                }
            );
        }
    };

    private readonly _isFormReady = (): boolean => {
        const { formItems } = this.state;
        // Strange state behavior -> workaround to check address field
        const checkVAT = formItems.find(formItem => {
            return formItem.id === InputID.VatId;
        });
        const checkProspectType = formItems.find(formItem => {
            return formItem.id === InputID.ProspectType;
        });
        if (checkProspectType && checkProspectType.value === '') {
            return false;
        }

        const checkAddress = formItems.find(formItems => {
            return formItems.id === InputID.Address;
        });

        const checkCountry = checkAddress?.address?.ThreeLetterISORegionName;

        if (checkCountry === 'CHE' && checkVAT?.isRequired === true) {
            return true;
        }

        for (const currentInput of formItems) {
            // Will return FALSE if:
            //  - input field is required
            //      IF is address AND addressHasErrors === true
            //      ELSE value is undefined OR empty
            //  OR
            //  - pattern provided and value does not match pattern

            if (currentInput.isRequired) {
                if (currentInput.type === InputType.Address) {
                    if (this.state.addressHasErrors) {
                        return false;
                    }
                } else if (currentInput.value === undefined || currentInput.value === '') {
                    return false;
                }
            }
            if (currentInput.pattern !== undefined && currentInput.value.match(currentInput.pattern) === null) {
                return false;
            }
        }

        return true;
    };

    // Define the callback that will be passed to FriendlyCaptcha
    private setCaptchaReady = (isReady: boolean) => {
        this.setState({ captchaReady: isReady });
    };

    private readonly _onSubmit = (event: React.MouseEvent<HTMLElement, MouseEvent>) => {
        if (this.state.addressHasErrors) {
            this.setState({
                showAddressErrors: true
            });

            event.preventDefault();
            return;
        }

        const { config, context } = this.props;

        if (config.showCaptcha && !this.state.captchaReady) {
            event.preventDefault(); // Prevent form submission if captcha is not solved
            return;
        }

        // Check for matching id and if not empty
        const firstName = this._getItem(InputID.FirstName);
        const lastName = this._getItem(InputID.LastName);
        const companyName = this._getItem(InputID.CompanyName);
        const companyEmail = this._getItem(InputID.Email);
        const companyAddress = this._getItem(InputID.Address, true);
        const department = this._getItem(InputID.Department);
        const vatNumber = this._getItem(InputID.VatId);
        const prospectType = this._getItem(InputID.ProspectType);
        // API gets email as part of the address object, but I do not use the email input from the address component, thus we need to copy it over
        if (companyEmail && companyEmail.value && companyAddress && companyAddress.address) {
            companyAddress.address.Email = companyEmail.value;
        }

        const ExtensionProperties: CommerceProperty[] = [
            {
                Key: 'JansenProspectType',
                Value: {
                    StringValue: prospectType && prospectType.value
                }
            },
            {
                Key: 'VatId',
                Value: {
                    StringValue: vatNumber && vatNumber.value
                }
            }
        ];

        const newPartner: BusinessPartnerProspectExtended = {
            FirstName: firstName && firstName.value,
            LastName: lastName && lastName.value,
            CompanyName: companyName && companyName.value,
            Address: companyAddress && companyAddress.address && companyAddress.address,
            Department: department && department.value,
            ProspectTypeValue: 1,
            ExtensionProperties: ExtensionProperties
        };

        this.setState({
            isReady: false
        });

        // Create business partner request
        createProspectAsync({ callerContext: context.actionContext }, newPartner)
            .then(() => {
                const successPageURL =
                    (config.confirmationLink && config.confirmationLink.linkUrl && config.confirmationLink.linkUrl.destinationUrl) ||
                    getUrlSync('home', this.props.context.actionContext) ||
                    '';
                window.location.href = successPageURL;
            })
            .catch(() => {
                this.setState({
                    hasSubmitErrors: true
                });
            });
    };

    private readonly _onCancel = () => {
        // Reset values of the form
        const cleanForm = this.state.formItems;
        cleanForm.forEach(input => {
            input.value = '';
        });

        // Set reset flag so address input will get reset on next renderInputSection() call
        this.needsToReset = true;

        this.setState({
            isReady: false,
            formItems: cleanForm,
            addressHasErrors: true,
            showAddressErrors: false,
            hasSubmitErrors: false
        });
    };

    private readonly _getItem = (id: string, isAddress: boolean = false) => {
        if (isAddress) {
            return this.state.formItems.find(item => {
                return item.id === id && item.address;
            });
        }
        return this.state.formItems.find(item => {
            return item.id === id && item.value !== '';
        });
    };
}

export default BusinessSignUp;
